import { useFormik } from 'formik';
import { useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as Yup from 'yup';
import AlertManager from '../../../../shared/components/global/AlertManager';
import StyledLink from '../../../../shared/components/global/StyledLink';
import { Button, ButtonSizes } from '../../../../shared/styles/button';
import { device } from '../../../../shared/styles/device';
import { Checkbox, Input, InputTextbox, Select, SelectTextbox } from '../../../../shared/styles/input';
import { FormError } from '../../../../shared/styles/validation';

const AcFormExtendedContainer = styled.div`
    a {
        display: inline !important;
        margin: unset !important;
    }
    p {
        text-align: left;
        margin-top: 0;
        margin-bottom: 8px;
    }
    .small-print {
        color: ${(props) => props.theme.colors.fontLight};
        font-size: 13px;
        line-height: 16px;
    }
`;
const InputRowDual = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  margin-bottom: 1rem;
  grid-row-gap: 1rem;
  
  @media ${device.tablet} {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-column-gap: 1rem;
  }
`;

const ACCheckboxContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 6px;
    margin-bottom: 6px;

    p {
        font-size: 16px;
        font-weight: 500;
        color: ${(props) => props.theme.colors.fontBlack};
        text-align: left;
        margin: 0;
    }
`;
const InputRowSingle = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  margin-bottom: 1rem;
`;
const ACForm = styled.form`
    max-width: 650px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    margin-top: 16px;
    
    @media ${device.tablet} {
        margin-top: 32px;
    }
    label {
        text-align: left;
    }
`;

export const ExtendedActivecampaignForm = () => {
    const [loading, setLoading] = useState(false)
    const [formSubmitted, setFormSubmitted] = useState(false)

    Yup.addMethod(Yup.object, 'atLeastOneOf', function (list) {
        return this.test({
            name: 'atLeastOneOf',
            message: '${path} must have at least one of these keys: ${keys}',
            exclusive: true,
            params: { keys: list.join(', ') },
            test: value => value == null || list.some(f => value[f] != null)
        })
    })

    const formik = useFormik({
        initialValues: {
            anrede: '',
            position: '',
            firma: '',
            lastname: '',
            firstname: '',
            email: '',
            cbSpeedRecruitingTalent: false,
            cbSpeedRecruitingCompany: false,
            cbJobNewsletter: false,
            cbDataprivacy: false,
            cbMailing: false
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email()
                .required('Pflichtfeld.'),
            cbDataprivacy: Yup.boolean()
                .isTrue('Pflichtfeld.'),
            cbMailing: Yup.boolean()
                .isTrue('Pflichtfeld.')
        }).shape({
            cbSpeedRecruitingTalent: Yup.boolean().when(['cbSpeedRecruitingCompany', 'cbJobNewsletter'], {
                is: (b, c) => !b && !c,
                then: Yup.boolean().isTrue('Pflichtfeld.')
            }),
            cbSpeedRecruitingCompany: Yup.boolean().when(['cbSpeedRecruitingTalent', 'cbJobNewsletter'], {
                is: (a, c) => !a && !c,
                then: Yup.boolean().isTrue('Pflichtfeld.')
            }),
            cbJobNewsletter: Yup.boolean().when(['cbSpeedRecruitingTalent', 'cbSpeedRecruitingCompany'], {
                is: (a, b) => !a && !b,
                then: Yup.boolean().isTrue('Pflichtfeld.')
            })
        }, [['cbSpeedRecruitingTalent', 'cbSpeedRecruitingCompany'], ['cbSpeedRecruitingTalent', 'cbJobNewsletter'], ['cbSpeedRecruitingCompany', 'cbJobNewsletter']]),

        onSubmit: (values: any) => {
            setLoading(true)


            /*             gtag.event({
                            action: 'submit_form',
                            category: 'Newsletter',
                            label: "",
                            value: ""
                        }); */
            let formData: FormData = new FormData();
            let newsletterAccepted = false;
            let dataPrivacyAccepted = false;
            for (var key in values) {
                let acKey = key;
                if (key === 'position') {
                    if (values[key]?.length > 0) {
                        acKey = "field[129]";
                    } else {
                        continue;
                    }
                } else if (key === 'firma') {
                    if (values[key]?.length > 0) {
                        acKey = "field[133]";
                    } else {
                        continue;
                    }
                } else if (key === 'anrede') {
                    if (values[key]?.length > 0) {
                        acKey = "field[115]";
                    } else {
                        continue;
                    }
                } else if (key === 'cbSpeedRecruitingTalent') {
                    if (values[key] === true) {
                        acKey = "field[101][]";
                        if (!newsletterAccepted) {
                            newsletterAccepted = true;
                            formData.append(acKey, "~|");
                        }
                        formData.append(acKey, "Ich interessiere mich für das nächste Speed-Recruiting als Bewerber*in.");
                    }
                    continue;
                } else if (key === 'cbSpeedRecruitingCompany') {
                    if (values[key] === true) {
                        acKey = "field[101][]";
                        if (!newsletterAccepted) {
                            newsletterAccepted = true;
                            formData.append(acKey, "~|");
                        }
                        formData.append(acKey, "Ich interessiere mich für das nächste Speed-Recruiting als Agentur.");
                    }
                    continue;
                } else if (key === 'cbJobNewsletter') {
                    if (values[key] === true) {
                        acKey = "field[101][]";
                        if (!newsletterAccepted) {
                            newsletterAccepted = true;
                            formData.append(acKey, "~|");
                        }
                        formData.append(acKey, "Ich möchte hiermit den Stell-Mich-Ein Job-Newsletter und weitere Informationen über Veranstaltungen und Angebote für meine berufliche Weiterentwicklung abonnieren und von  SCM kostenfrei per E-Mail und Post erhalten.");
                    }
                    continue;
                } else if (key === 'cbDataprivacy') {
                    if (values[key] === true) {
                        acKey = "field[26][]";
                        if (!dataPrivacyAccepted) {
                            dataPrivacyAccepted = true;
                            formData.append(acKey, "~|");
                        }
                        formData.append(acKey, "Ich habe die vorstehende Einverständnis-Erklärung gelesen und bin einverstanden.");
                    }
                    continue;
                } else if (key === 'cbMailing') {
                    if (values[key] === true) {
                        acKey = "field[26][]";
                        if (!dataPrivacyAccepted) {
                            dataPrivacyAccepted = true;
                            formData.append(acKey, "~|");
                        }
                        formData.append(acKey, "Ich stimme zu, E-Mail-Mitteilungen von der SCM zu erhalten.");
                    }
                    continue;
                }
                formData.append(acKey, values[key]);
            }

            formData.append("u", "25");
            formData.append("f", "25");
            formData.append("s", "");
            formData.append("c", "0");
            formData.append("m", "0");
            formData.append("act", "sub");
            formData.append("v", "2");
            formData.append("or", "29b1464d31513e1050e7898ecd1d28e0");
            formData.append("jsonp", "true");

            fetch("https://scmonline.activehosted.com/proc.php", {
                method: "POST",
                body: formData,
                mode: "no-cors",
            })
                .then(() => {
                    setLoading(false);
                    setFormSubmitted(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                    }, 5000);
                    formik.resetForm();
                    Swal.fire(AlertManager.ActiveCampaignMessageSuccess());
                })
                .catch(error => {
                    setLoading(false);
                    Swal.fire(AlertManager.ActiveCampaignMessageError(error, formData));
                    console.error("Request failed", error);
                });
        },
    });


    return (
        <AcFormExtendedContainer>
            <ACForm onSubmit={formik.handleSubmit}>
                <InputRowSingle>
                    <SelectTextbox>
                        <Select
                            id="anrede"
                            className={`${formik.values.anrede?.length > 0 ? "form-dropdown-select " : "form-dropdown-selectnone"} form-dropdown-input`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            name="anrede"
                            value={formik.values.anrede}
                            error={formik.touched.anrede && formik.touched.anrede && formik.errors.anrede != null}
                        >
                            <option value=""></option>
                            <option value="Herr">Herr</option>
                            <option value="Frau">Frau</option>
                        </Select>
                        <span className='form-dropdown-label'>Anrede</span>
                    </SelectTextbox>
                </InputRowSingle>
                <InputRowDual>
                    <InputTextbox>
                        <Input
                            id="firstname"
                            className={`${formik.values.firstname?.length != null && formik.values.firstname?.length > 0 ? "form-textbox-entered " : ""} form-textbox-input`}
                            name="firstname"
                            type="text"
                            value={formik.values.firstname}
                            error={formik.touched.firstname && formik.errors.firstname != null}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span className='form-textbox-label'>Vorname</span>
                    </InputTextbox>
                    <InputTextbox>
                        <Input
                            id="lastname"
                            className={`${formik.values.lastname?.length != null && formik.values.lastname?.length > 0 ? "form-textbox-entered " : ""} form-textbox-input`}
                            name="lastname"
                            type="text"
                            value={formik.values.lastname}
                            error={formik.touched.lastname && formik.errors.lastname != null}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span className='form-textbox-label'>Nachname</span>
                    </InputTextbox>
                </InputRowDual>
                <InputRowDual>
                    <InputTextbox>
                        <Input
                            id="firma"
                            className={`${formik.values.firma?.length != null && formik.values.firma?.length > 0 ? "form-textbox-entered " : ""} form-textbox-input`}
                            name="firma"
                            type="text"
                            value={formik.values.firma}
                            error={formik.touched.firma && formik.errors.firma != null}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span className='form-textbox-label'>Firma</span>
                    </InputTextbox>
                    <InputTextbox>
                        <Input
                            id="position"
                            className={`${formik.values.position?.length != null && formik.values.position?.length > 0 ? "form-textbox-entered " : ""} form-textbox-input`}
                            name="position"
                            type="text"
                            value={formik.values.position}
                            error={formik.touched.position && formik.errors.position != null}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span className='form-textbox-label'>Position</span>
                    </InputTextbox>
                </InputRowDual>


                <InputRowSingle>
                    <InputTextbox>
                        <Input
                            id="email"
                            className={`${formik.values.email?.length != null && formik.values.email?.length > 0 ? "form-textbox-entered " : ""} form-textbox-input`}
                            name="email"
                            type="text"
                            value={formik.values.email}
                            error={formik.touched.email && formik.errors.email != null}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span className='form-textbox-label'>E-Mail*</span>
                    </InputTextbox>
                    {formik.touched.email && formik.errors.email ?
                        <div className='form-message-wrapper'>
                            <FormError errorText={formik.errors.email} />
                        </div>
                        : null}
                </InputRowSingle>

                <p><strong>Newsletter*</strong></p>
                <InputRowSingle>
                    <ACCheckboxContainer>
                        <Checkbox
                            id="cbSpeedRecruitingTalent"
                            name="cbSpeedRecruitingTalent"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="checkbox"
                            checked={formik.values.cbSpeedRecruitingTalent}
                            error={formik.touched.cbSpeedRecruitingTalent && formik.errors.cbSpeedRecruitingTalent != null}
                        />
                        <label htmlFor="checkbox">Ich interessiere mich für das nächste Speed-Recruiting als Bewerber*in.</label>
                    </ACCheckboxContainer>
                </InputRowSingle>
                <InputRowSingle>
                    <ACCheckboxContainer>
                        <Checkbox
                            id="cbSpeedRecruitingCompany"
                            name="cbSpeedRecruitingCompany"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="checkbox"
                            checked={formik.values.cbSpeedRecruitingCompany}
                            error={formik.touched.cbSpeedRecruitingCompany && formik.errors.cbSpeedRecruitingCompany != null}
                        />
                        <label htmlFor="checkbox">Ich interessiere mich für das nächste Speed-Recruiting als Agentur.</label>
                    </ACCheckboxContainer>
                </InputRowSingle>
                <InputRowSingle>
                    <ACCheckboxContainer>
                        <Checkbox
                            id="cbJobNewsletter"
                            name="cbJobNewsletter"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="checkbox"
                            checked={formik.values.cbJobNewsletter}
                            error={formik.touched.cbJobNewsletter && formik.errors.cbJobNewsletter != null}
                        />
                        <label htmlFor="checkbox">Ich möchte hiermit den Stell-Mich-Ein Job-Newsletter und weitere Informationen über Veranstaltungen und Angebote für meine berufliche Weiterentwicklung abonnieren und von  SCM kostenfrei per E-Mail und Post erhalten.</label>
                    </ACCheckboxContainer>
                </InputRowSingle>
                <p><strong>Einverständniserklärung</strong></p>
                <p className='small-print'>Die SCM verpflichtet sich, Ihre Privatsphäre zu schützen und zu respektieren. Von Zeit zu Zeit möchten wir Sie über Trends aus Kommunikation & Marketing informieren. Wenn Sie damit einverstanden sind, dass wir Sie zu diesem Zweck kontaktieren, aktivieren Sie bitte das folgende Kontrollkästchen. Diese Einwilligung kann jederzeit widerrufen werden.</p>
                <p className='small-print'>Um Ihnen die gewünschten Inhalte bereitzustellen, müssen wir Ihre personenbezogenen Daten speichern und verarbeiten. Wenn Sie damit einverstanden sind, dass wir Ihre personenbezogenen Daten für diesen Zweck speichern, aktivieren Sie bitte das folgende Kontrollkästchen. Die Speicherung und Verarbeitung Ihrer Daten können Sie jederzeit widerrufen. Weitere Informationen zu unseren Datenschutzverfahren und dazu, wie wir Ihre Privatsphäre schützen und respektieren, finden Sie in unserer <StyledLink href="/datenschutz">Datenschutzrichtinie</StyledLink>.</p>
                <InputRowSingle>
                    <ACCheckboxContainer>
                        <Checkbox
                            id="cbDataprivacy"
                            name="cbDataprivacy"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="checkbox"
                            checked={formik.values.cbDataprivacy}
                            error={formik.touched.cbDataprivacy && formik.errors.cbDataprivacy != null}
                        />
                        <label htmlFor="checkbox"> Ich habe die vorstehende Einverständnis-Erklärung gelesen und bin einverstanden.</label>
                    </ACCheckboxContainer>
                </InputRowSingle>
                <InputRowSingle>
                    <ACCheckboxContainer>
                        <Checkbox
                            id="cbMailing"
                            name="cbMailing"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="checkbox"
                            checked={formik.values.cbMailing}
                            error={formik.touched.cbMailing && formik.errors.cbMailing != null}
                        />
                        <label htmlFor="checkbox">Ich stimme zu, E-Mail-Mitteilungen von der SCM zu erhalten.</label>
                    </ACCheckboxContainer>
                </InputRowSingle>
                <InputRowSingle>
                    <Button size={ButtonSizes.m} disabled={loading || !formik.isValid} $loading={loading} primary type="submit">
                        Newsletter abonnieren
                    </Button>
                </InputRowSingle>
            </ACForm>
        </AcFormExtendedContainer>
    );

}
